import { MAIN_STAGE } from '../main-stage';
import type { EdgeSide, TrapDraw, SetDefinition } from '../types';
import { AMBYSS } from './ambyss';
import { DOME } from './dome';
import { PUMPUI } from './pumpui';

export type PathAxis = 'across' | 'down';

export interface ExitDefinition {
  side: EdgeSide;
  at: number;
}

export type AlleyExtra = 'chai' | 'bracelets' | 'crepes' | 'thrift';

export type TrailExtra = 'houses' | 'islet' | 'torii' | 'spirits' | 'jetty' | 'cairns';

export const CHALLENGE_IDS = ['rapides', 'nombreuses', 'boss'] as const;

export type ChallengeId = (typeof CHALLENGE_IDS)[number];

// Compounded on what the bad vibes of the stage ahead get; a missing factor is 1.
export interface ChallengeScale {
  // Every bad vibe but the bosses.
  speed?: number;
  // The bad vibes of each spawn rule.
  count?: number;
  bossHp?: number;
  bossDamage?: number;
}

export interface ChallengeDefinition {
  id: ChallengeId;
  scale: ChallengeScale;
  // Added to 1 for the multiplier of what the stage ahead earns.
  score: number;
}

export const ALLEY_STANDS: readonly AlleyExtra[] = ['chai', 'bracelets', 'crepes', 'thrift'];

const OFTEN = 2 / 3;
const SOMETIMES = 1 / 3;

export interface FestivalDefinition {
  // Each played once, in the order their exits open.
  setIds: readonly string[];
  // Compounded on the life and hits of every bad vibe for each stage held.
  perStageHeld: { hp: number; damage: number };
  // The carried traps of the last stage: mostly Lasers, and its first a sure Ventilateur.
  lastStageTraps: TrapDraw;
  // Each trap still placed when a stage holds bursts into `drops` vibes of `amount`, thrown `radius` around.
  trapVibes: { drops: number; amount: number; radius: number; flightBeats: number };
  departBars: number;
  // The boards along each path: shot by every player, each makes the stage ahead harder.
  challenges: {
    list: readonly ChallengeDefinition[];
    // A shot toward a board, from no farther than `reach` from its rim, lights it.
    radius: number;
    reach: number;
    // A player this close has the assist aim at a board they have not shot yet.
    assistReach: number;
  };
  exit: { radius: number; depth: number };
  // One exit per stage still to play, in `setIds` order, clear of the speakers, obstacles and decor.
  exits: Readonly<Record<string, readonly ExitDefinition[]>>;
  path: {
    length: number;
    width: number;
    endDepth: number;
    alley: Readonly<Record<AlleyExtra, number>>;
    alleyStands: number;
    trailStageIds: readonly string[];
    trail: Readonly<Record<TrailExtra, number>>;
  };
}

export const FESTIVAL: FestivalDefinition = {
  setIds: [MAIN_STAGE.id, PUMPUI.id, DOME.id, AMBYSS.id],
  perStageHeld: { hp: 1.3, damage: 1.3 },
  lastStageTraps: {
    first: 'ventilateur-brumisant',
    weights: { laser: 6, 'caisson-de-basse': 2, 'ventilateur-brumisant': 0.1 },
  },
  trapVibes: { drops: 5, amount: 1, radius: 50, flightBeats: 1 },
  departBars: 2,
  challenges: {
    list: [
      { id: 'rapides', scale: { speed: 1.25 }, score: 0.25 },
      { id: 'nombreuses', scale: { count: 1.3 }, score: 0.25 },
      { id: 'boss', scale: { bossHp: 1.5 }, score: 0.25 },
    ],
    radius: 48,
    reach: 420,
    assistReach: 60,
  },
  exit: { radius: 80, depth: 90 },
  exits: {
    [MAIN_STAGE.id]: [
      { side: 'right', at: 780 },
      { side: 'top', at: 1200 },
      { side: 'bottom', at: 400 },
    ],
    [PUMPUI.id]: [
      { side: 'left', at: 500 },
      { side: 'top', at: 800 },
      { side: 'right', at: 500 },
    ],
    [DOME.id]: [
      { side: 'left', at: 500 },
      { side: 'right', at: 500 },
      { side: 'bottom', at: 800 },
    ],
    [AMBYSS.id]: [
      { side: 'left', at: 500 },
      { side: 'right', at: 500 },
      { side: 'top', at: 800 },
    ],
  },
  path: {
    length: 3600,
    width: 800,
    endDepth: 160,
    alley: {
      chai: OFTEN,
      bracelets: OFTEN,
      crepes: SOMETIMES,
      thrift: SOMETIMES,
    },
    alleyStands: 2,
    trailStageIds: [AMBYSS.id],
    trail: {
      houses: OFTEN,
      islet: OFTEN,
      torii: SOMETIMES,
      spirits: SOMETIMES,
      jetty: SOMETIMES,
      cairns: SOMETIMES,
    },
  },
};

export function pathLook(fromId: string | undefined, toId: string | undefined): 'alley' | 'trail' {
  const { trailStageIds } = FESTIVAL.path;
  return [fromId, toId].some((id) => id !== undefined && trailStageIds.includes(id))
    ? 'trail'
    : 'alley';
}

export function exitSpot(
  arena: { width: number; height: number },
  { side, at }: ExitDefinition,
): { x: number; y: number; radius: number } {
  const { radius, depth } = FESTIVAL.exit;
  switch (side) {
    case 'left':
      return { x: depth, y: at, radius };
    case 'right':
      return { x: arena.width - depth, y: at, radius };
    case 'top':
      return { x: at, y: depth, radius };
    case 'bottom':
      return { x: at, y: arena.height - depth, radius };
  }
}

export function entrySpot(
  arena: { width: number; height: number },
  stageId: string,
  side: EdgeSide,
): { x: number; y: number; radius: number } {
  const place = (FESTIVAL.exits[stageId] ?? []).find((exit) => exit.side === side);
  const middle = side === 'left' || side === 'right' ? arena.height / 2 : arena.width / 2;
  return exitSpot(arena, { side, at: place?.at ?? middle });
}

export const OPPOSITE: Readonly<Record<EdgeSide, EdgeSide>> = {
  left: 'right',
  right: 'left',
  top: 'bottom',
  bottom: 'top',
};

export const axisOf = (side: EdgeSide): PathAxis =>
  side === 'left' || side === 'right' ? 'across' : 'down';

export const pathId = (arrivalId: string, axis: PathAxis) => `chemin-${arrivalId}-${axis}`;

// No tier and no speaker: nothing comes. What blocks depends on the game (`pathWithObstacles`).
function pathTo(arrival: SetDefinition, axis: PathAxis): SetDefinition {
  const { length, width } = FESTIVAL.path;
  return {
    id: pathId(arrival.id, axis),
    name: arrival.name,
    ...(arrival.style === undefined ? {} : { style: arrival.style }),
    ...(arrival.ticksPerBeat === undefined ? {} : { ticksPerBeat: arrival.ticksPerBeat }),
    ...(arrival.acoustics === undefined ? {} : { acoustics: arrival.acoustics }),
    ...(arrival.weaponSlots === undefined ? {} : { weaponSlots: arrival.weaponSlots }),
    ...(arrival.reviveBars === undefined ? {} : { reviveBars: arrival.reviveBars }),
    ...(arrival.trapWear === undefined ? {} : { trapWear: arrival.trapWear }),
    decor: 'chemin',
    trackIds: arrival.trackIds,
    arena: axis === 'across' ? { width: length, height: width } : { width, height: length },
    // No core to defend: a core without a radius is not drawn.
    core: { radius: 0, maxHp: arrival.core.maxHp },
    levelCurve: arrival.levelCurve,
    pickups: arrival.pickups,
    tiers: [],
  };
}

export const FESTIVAL_PATHS: readonly SetDefinition[] = [MAIN_STAGE, PUMPUI, DOME, AMBYSS].flatMap(
  (stage) => [pathTo(stage, 'across'), pathTo(stage, 'down')],
);
